<template>
    <MobileDrawer
        :isVisible="show"
        :title="drawerTitle"
        :closeOnOverlay="true"
        :swipeToClose="true"
        :maxHeight="'75vh'"
        @close="handleClose"
    >
        <div v-if="isIOS" class="pwa-prompt-content">
            <div class="pwa-prompt-icon">
                <i class="ri-smartphone-line"></i>
            </div>

            <p class="pwa-prompt-description">Für schnellen Zugriff direkt vom Homescreen:</p>

            <div class="pwa-prompt-steps">
                <div class="pwa-prompt-step">
                    <div class="step-number">1</div>
                    <div class="step-content">
                        <strong>Tippe auf das Teilen-Symbol</strong>
                        <span class="step-hint"
                            ><i class="ri-share-2-line"></i> in der unteren Leiste</span
                        >
                    </div>
                </div>

                <div class="pwa-prompt-step">
                    <div class="step-number">2</div>
                    <div class="step-content">
                        <strong>Scrolle runter und wähle</strong>
                        <span class="step-hint">"Zum Home-Bildschirm hinzufügen"</span>
                    </div>
                </div>

                <div class="pwa-prompt-step">
                    <div class="step-number">3</div>
                    <div class="step-content">
                        <strong>Tippe auf "Hinzufügen"</strong>
                    </div>
                </div>
            </div>

            <div class="pwa-prompt-benefits">
                <div class="benefit-item">
                    <i class="ri-checkbox-circle-line"></i>
                    <span>Offline verfügbar</span>
                </div>
                <div class="benefit-item">
                    <i class="ri-checkbox-circle-line"></i>
                    <span>Push-Benachrichtigungen</span>
                </div>
                <div class="benefit-item">
                    <i class="ri-checkbox-circle-line"></i>
                    <span>Schnellerer Start</span>
                </div>
            </div>

            <div class="pwa-prompt-actions">
                <Button variant="success" size="large" @onClick="handleUnderstood">
                    Alles klar!
                </Button>
                <Button variant="plain" size="small" @onClick="handleDontShowAgain">
                    Nicht mehr anzeigen
                </Button>
            </div>
        </div>

        <div v-else-if="isAndroid && canInstall" class="pwa-prompt-content">
            <div class="pwa-prompt-icon">
                <i class="ri-smartphone-line"></i>
            </div>

            <p class="pwa-prompt-description">Installiere TaxiFusion für:</p>

            <div class="pwa-prompt-benefits">
                <div class="benefit-item">
                    <i class="ri-checkbox-circle-line"></i>
                    <span>Schnelleren Zugriff</span>
                </div>
                <div class="benefit-item">
                    <i class="ri-checkbox-circle-line"></i>
                    <span>Offline-Nutzung</span>
                </div>
                <div class="benefit-item">
                    <i class="ri-checkbox-circle-line"></i>
                    <span>Benachrichtigungen</span>
                </div>
            </div>

            <div class="pwa-prompt-actions">
                <Button
                    variant="success"
                    size="large"
                    @onClick="handleInstall"
                    :isLoading="isInstalling"
                >
                    Installieren
                </Button>
                <Button variant="plain" size="small" @onClick="handleDismiss"> Nein danke </Button>
            </div>
        </div>

        <div v-else-if="isAndroid && !canInstall" class="pwa-prompt-content">
            <div class="pwa-prompt-icon">
                <i class="ri-smartphone-line"></i>
            </div>

            <p class="pwa-prompt-description">Installiere TaxiFusion für schnellen Zugriff:</p>

            <div class="pwa-prompt-steps">
                <div class="pwa-prompt-step">
                    <div class="step-number">1</div>
                    <div class="step-content">
                        <strong>Tippe auf das Menü-Symbol</strong>
                        <span class="step-hint"
                            ><i class="ri-more-2-fill"></i> oben rechts im Browser</span
                        >
                    </div>
                </div>

                <div class="pwa-prompt-step">
                    <div class="step-number">2</div>
                    <div class="step-content">
                        <strong>Wähle "Zum Startbildschirm hinzufügen"</strong>
                        <span class="step-hint">oder "App installieren"</span>
                    </div>
                </div>

                <div class="pwa-prompt-step">
                    <div class="step-number">3</div>
                    <div class="step-content">
                        <strong>Bestätige die Installation</strong>
                    </div>
                </div>
            </div>

            <div class="pwa-prompt-benefits">
                <div class="benefit-item">
                    <i class="ri-checkbox-circle-line"></i>
                    <span>Schnellerer Zugriff</span>
                </div>
                <div class="benefit-item">
                    <i class="ri-checkbox-circle-line"></i>
                    <span>Offline-Nutzung</span>
                </div>
                <div class="benefit-item">
                    <i class="ri-checkbox-circle-line"></i>
                    <span>Benachrichtigungen</span>
                </div>
            </div>

            <div class="pwa-prompt-actions">
                <Button variant="success" size="large" @onClick="handleUnderstood">
                    Alles klar!
                </Button>
                <Button variant="plain" size="small" @onClick="handleDontShowAgain">
                    Nicht mehr anzeigen
                </Button>
            </div>
        </div>
    </MobileDrawer>
</template>

<script>
import pwaService from '@/services/pwaService';
import { trackPWA } from '@/services/analytics';
import Button from '@/components/widgets/Button';
import MobileDrawer from '@/components/widgets/MobileDrawer.vue';

export default {
    name: 'PWAInstallPrompt',
    components: {
        Button,
        MobileDrawer,
    },
    data() {
        return {
            show: false,
            isInstalling: false,
        };
    },
    computed: {
        isIOS() {
            return pwaService.isIOS();
        },
        isAndroid() {
            return pwaService.isAndroid();
        },
        canInstall() {
            return pwaService.canInstall();
        },
        installMethod() {
            return pwaService.getInstallMethod();
        },
        drawerTitle() {
            if (this.isIOS) {
                return 'TaxiFusion installieren';
            }
            if (this.isAndroid && this.canInstall) {
                return 'App installieren?';
            }
            if (this.isAndroid && !this.canInstall) {
                return 'TaxiFusion installieren';
            }
            return 'App installieren';
        },
    },
    methods: {
        showPrompt(force = false) {
            if (!force && !pwaService.shouldShowPrompt()) {
                return false;
            }
            this.show = true;
            if (!force) {
                trackPWA.promptShown(
                    this.isIOS ? 'ios' : this.isAndroid ? 'android' : 'other',
                    'auto',
                );
            }
            return true;
        },

        hidePrompt() {
            this.show = false;
        },

        handleClose() {
            this.handleDismiss();
        },

        async handleInstall() {
            this.isInstalling = true;
            trackPWA.installClicked('android');

            try {
                const outcome = await pwaService.promptInstall();

                if (outcome === 'accepted') {
                    trackPWA.installAccepted('android');
                    this.hidePrompt();
                } else {
                    trackPWA.installDeclined('android');
                    this.handleDismiss();
                }
            } catch (error) {
                trackPWA.installError('android', error.message);
                this.handleDismiss();
            } finally {
                this.isInstalling = false;
            }
        },

        handleUnderstood() {
            pwaService.markPromptAsShown();
            trackPWA.installDismissed('ios', 'understood');
            this.hidePrompt();
        },

        handleDismiss() {
            pwaService.markPromptAsDeclined(false);
            trackPWA.installDismissed(
                this.isIOS ? 'ios' : this.isAndroid ? 'android' : 'other',
                'dismiss',
            );
            this.hidePrompt();
        },

        handleDontShowAgain() {
            pwaService.markPromptAsDeclined(true);
            trackPWA.installDismissed(
                this.isIOS ? 'ios' : this.isAndroid ? 'android' : 'other',
                'dont_show_again',
            );
            this.hidePrompt();
        },
    },
};
</script>

<style lang="scss" scoped>
.pwa-prompt-content {
    text-align: center;
}

.pwa-prompt-icon {
    text-align: center;
    margin-bottom: 12px;

    i {
        font-size: 40px;
        color: var(--color-blue-dark, #2e3358);
    }
}

.pwa-prompt-description {
    font-size: 15px;
    text-align: center;
    color: var(--color-text-gray, #666);
    margin-bottom: 16px;
    line-height: 1.4;
}

.pwa-prompt-steps {
    margin: 16px 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.pwa-prompt-step {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 10px;
    background: var(--color-bg-light, #f5f5f5);
    border-radius: 8px;
    text-align: left;
}

.step-number {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--color-blue-dark, #2e3358);
    color: white;
    border-radius: 50%;
    font-size: 14px;
    font-weight: 600;
    flex-shrink: 0;
}

.step-content {
    display: flex;
    flex-direction: column;
    gap: 4px;

    strong {
        font-size: 15px;
        color: var(--color-text-black, #1a1a1a);
    }

    .step-hint {
        font-size: 13px;
        color: var(--color-text-gray, #666);
        display: flex;
        align-items: center;
        gap: 4px;

        i {
            font-size: 14px;
        }
    }
}

.pwa-prompt-benefits {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin: 16px 0;
    padding: 12px;
    background: var(--color-bg-light, #f5f5f5);
    border-radius: 8px;
    text-align: left;
}

.benefit-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
    color: var(--color-text-black, #1a1a1a);

    i {
        font-size: 18px;
        color: var(--color-green-dark, #27ae60);
    }
}

.pwa-prompt-actions {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 16px;
}

@media (max-width: 500px) {
    .pwa-prompt-description {
        font-size: 14px;
    }

    .pwa-prompt-icon i {
        font-size: 36px;
    }
}
</style>
